import React from 'react';
import {
    Box,
    Chip,
    IconButton,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    ChangeStatusIcon,
    CheckCircleIcon,
    CTableWrapper,
    DeleteIcon,
    EditIcon,
    LinkIcon,
    ViewIcon,
    WarningIcon,
} from '../../../../../dum';
import { PdfIcon } from '../../../../../smart';
import {
    formatDate,
    formatFloat,
    formatRelativeDate,
} from '../../../../../../helpers';
import { OrderQuotationsPageQuery } from '../../../../../../services';

type OrderQuotationDoc =
    OrderQuotationsPageQuery['paginatedOrderQuotations']['docs'][number];

type OrderQuotationProductDoc =
    OrderQuotationDoc['order_quotation_products'][number];

interface IOrderQuotationCard {
    orderQuotation: OrderQuotationDoc;
    // Optional, sales-gated. Hidden instead of disabled when the quotation is
    // not editable, so the action row only shows operations valid right now.
    onEdit?: (orderQuotation: OrderQuotationDoc) => void;
    // Optional, admin-gated. Hidden unless deletion is valid for this row.
    onDelete?: (orderQuotation: OrderQuotationDoc) => void;
    // Optional, admin-gated. The icon only renders when the handler is provided
    // (the list block passes it only for admins), mirroring the context menu.
    onChangeStatus?: (orderQuotation: OrderQuotationDoc) => void;
    // Optional, admin-gated. Opens the acceptance screen. Only rendered for
    // admins; the button itself is disabled until the quotation is acceptable.
    onAccept?: (orderQuotation: OrderQuotationDoc) => void;
    // Optional, sales-gated. Links a persisted free line to a real product.
    // Rendered only when the quotation still has free lines and is Borrador or
    // Enviada (the backend allows linking only before acceptance starts).
    onLinkProduct?: (orderQuotation: OrderQuotationDoc) => void;
    // Prints the cotización PDF. Read-only, always shown (the page is gated to
    // SALES_DOMAIN, so anyone who can see the card can print it).
    onPrint: (orderQuotation: OrderQuotationDoc) => void;
}

// The per-line importe. There is no server-side `total` ResolveField on
// OrderQuotationProduct (unlike OrderRequestProduct), so it is computed here with
// the exact formula the backend uses for the quotation subtotal
// (kilo_price·kilos + group_price·groups). A line carries one or the other price.
function lineTotal(orp: OrderQuotationProductDoc): number {
    return orp.kilo_price * orp.kilos + orp.group_price * orp.groups;
}

/**
 * Card body for one cotización inside the Cotizaciones CCardList. Header lines
 * (código / estatus / vencida marker / fecha, then cliente / entrega estimada /
 * vigencia / condiciones de pago), the quoted-products sub-table with a free-line
 * indicator, the subtotal / IVA / total readout, and the row actions.
 */
export function OrderQuotationCard({
    orderQuotation,
    onEdit,
    onDelete,
    onChangeStatus,
    onAccept,
    onLinkProduct,
    onPrint,
}: IOrderQuotationCard) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const subBaseColor = isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff';
    const subStripeColor = isDark ? 'rgba(59,130,246,0.16)' : '#dbeafe';

    const products = orderQuotation.order_quotation_products ?? [];

    // A free line still awaiting its real product. Linking is only meaningful
    // while the quotation is Borrador (1) or Enviada (2) — the backend refuses it
    // once acceptance starts, and free lines block acceptance anyway.
    const statusId = orderQuotation.order_quotation_status?.id;
    const hasFreeLines = products.some((orp) => orp.product_id == null);
    const canLink =
        !!onLinkProduct && hasFreeLines && (statusId === 1 || statusId === 2);

    // Acceptance has started once the catalog was written (always first) or the
    // whole thing completed / the status is Aceptada. After that the quotation is
    // locked: status changes, rejection, details edits and deletion are all
    // refused by the backend, so the UI hides those actions too. See the plan,
    // "Lock quotation changes after acceptance starts".
    const acceptanceStarted =
        !!orderQuotation.catalog_step_done ||
        !!orderQuotation.acceptance_complete ||
        statusId === 3;
    const canEdit = !!onEdit && orderQuotation.is_editable;
    const canDelete = !!onDelete && orderQuotation.is_deletable;

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 0.5,
                }}
            >
                <Typography variant={'subtitle2'}>
                    #{orderQuotation.order_code}
                </Typography>
                {orderQuotation.order_quotation_status ? (
                    <Chip
                        size={'small'}
                        label={orderQuotation.order_quotation_status.name}
                    />
                ) : null}
                {orderQuotation.is_expired ? (
                    <Chip
                        size={'small'}
                        color={'warning'}
                        variant={'outlined'}
                        icon={<WarningIcon />}
                        label={'Vencida'}
                    />
                ) : null}
                {/* Partial acceptance: the catalog step ran (always first) but
                    the whole thing did not complete. Surfaces the half-state on
                    the row so it isn't discoverable only by opening the dialog. */}
                {orderQuotation.catalog_step_done &&
                !orderQuotation.acceptance_complete ? (
                    <Chip
                        size={'small'}
                        color={'warning'}
                        variant={'filled'}
                        icon={<WarningIcon />}
                        label={'Aceptación incompleta'}
                    />
                ) : null}
                <Box sx={{ flex: 1 }} />
                <Typography variant={'body2'}>
                    {formatDate(orderQuotation.date)}
                    {orderQuotation.updated_at ? (
                        <Typography
                            component={'span'}
                            variant={'caption'}
                            color={'text.secondary'}
                            sx={{ ml: 0.5 }}
                        >
                            (act.{' '}
                            {formatRelativeDate(orderQuotation.updated_at)}
                            {orderQuotation.updated_by
                                ? ` por ${orderQuotation.updated_by.fullname}`
                                : ''}
                            )
                        </Typography>
                    ) : null}
                </Typography>
            </Box>
            <Typography variant={'body2'} sx={{ mb: 1 }}>
                <b>{orderQuotation.account?.name}</b>
                {orderQuotation.estimated_delivery_date
                    ? ` · entrega estimada ${formatDate(
                          orderQuotation.estimated_delivery_date,
                      )}`
                    : ''}
                {orderQuotation.expiration_date
                    ? ` · vigencia ${formatDate(
                          orderQuotation.expiration_date,
                      )}`
                    : ' · sin vencimiento'}
            </Typography>
            {orderQuotation.payment_terms ? (
                <Typography
                    variant={'caption'}
                    color={'text.secondary'}
                    display={'block'}
                    sx={{ mb: 0.5 }}
                >
                    Condiciones de pago: {orderQuotation.payment_terms}
                </Typography>
            ) : null}
            {orderQuotation.notes ? (
                <Typography
                    variant={'caption'}
                    color={'text.secondary'}
                    display={'block'}
                    sx={{ mb: 1 }}
                >
                    {orderQuotation.notes}
                </Typography>
            ) : null}
            <CTableWrapper
                padding={false}
                baseColor={subBaseColor}
                stripeColor={subStripeColor}
                items={[
                    {
                        title: 'Producto',
                        cellGrow: 55,
                        // A linked line renders the product's external_description;
                        // a free line (product_id NULL) renders its
                        // proposed_description marked as "Línea libre", since a free
                        // line is what blocks acceptance until Producción creates
                        // the real product.
                        content: (orp) =>
                            orp.product_id != null ? (
                                orp.product?.external_description
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Chip
                                        size={'small'}
                                        color={'warning'}
                                        variant={'outlined'}
                                        label={'Línea libre'}
                                    />
                                    <span>{orp.proposed_description}</span>
                                </Box>
                            ),
                    },
                    {
                        title: 'Bultos',
                        cellGrow: 15,
                        textAlign: 'right',
                        content: (orp) => formatFloat(orp.groups),
                    },
                    {
                        title: 'Kilos',
                        cellGrow: 15,
                        textAlign: 'right',
                        content: (orp) => formatFloat(orp.kilos),
                    },
                    {
                        title: 'Importe',
                        cellGrow: 15,
                        textAlign: 'right',
                        content: (orp) => formatFloat(lineTotal(orp)),
                    },
                ]}
                data={products}
            />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    mt: 1,
                }}
            >
                <Typography variant={'caption'} color={'text.secondary'}>
                    Subtotal: {formatFloat(orderQuotation.subtotal)}
                </Typography>
                {orderQuotation.require_tax ? (
                    <Typography variant={'caption'} color={'text.secondary'}>
                        IVA: {formatFloat(orderQuotation.tax)}
                    </Typography>
                ) : null}
                <Typography variant={'caption'}>
                    <b>Total: {formatFloat(orderQuotation.total)}</b>
                </Typography>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 0.5,
                    mt: 1,
                }}
            >
                {canEdit ? (
                    <IconButton
                        size={'small'}
                        onClick={(e) => {
                            // The whole card is clickable (opens edit) — keep the
                            // buttons from double-firing through it.
                            e.stopPropagation();
                            onEdit!(orderQuotation);
                        }}
                    >
                        <EditIcon />
                    </IconButton>
                ) : null}
                {onChangeStatus && !acceptanceStarted ? (
                    <Tooltip arrow title={'Cambiar status'}>
                        <IconButton
                            size={'small'}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChangeStatus(orderQuotation);
                            }}
                        >
                            <ChangeStatusIcon />
                        </IconButton>
                    </Tooltip>
                ) : null}
                {canLink ? (
                    <Tooltip arrow title={'Vincular producto a línea libre'}>
                        <IconButton
                            size={'small'}
                            color={'warning'}
                            onClick={(e) => {
                                e.stopPropagation();
                                onLinkProduct!(orderQuotation);
                            }}
                        >
                            <LinkIcon />
                        </IconButton>
                    </Tooltip>
                ) : null}
                {onAccept && (statusId === 2 || statusId === 3) ? (
                    <Tooltip
                        arrow
                        title={statusId === 3 ? 'Ver aceptación' : 'Aceptar'}
                    >
                        {/* Enviada gets the actionable acceptance icon. An
                            accepted quotation keeps a read-only entry point to
                            the same preview, but uses ViewIcon so it cannot be
                            mistaken for an action that can run again. */}
                        <IconButton
                            size={'small'}
                            color={statusId === 3 ? 'primary' : 'success'}
                            onClick={(e) => {
                                e.stopPropagation();
                                onAccept(orderQuotation);
                            }}
                        >
                            {statusId === 3 ? (
                                <ViewIcon />
                            ) : (
                                <CheckCircleIcon />
                            )}
                        </IconButton>
                    </Tooltip>
                ) : null}
                {canDelete ? (
                    <IconButton
                        size={'small'}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete!(orderQuotation);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                ) : null}
                <Tooltip arrow title={'Imprimir pdf'}>
                    <IconButton
                        size={'small'}
                        onClick={(e) => {
                            e.stopPropagation();
                            onPrint(orderQuotation);
                        }}
                    >
                        <PdfIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
}
